import { type RefObject, useLayoutEffect, useState } from 'react'

export type ScrollEdges = {
  /** Content is clipped above the visible area — the container is scrolled down. */
  hasAbove: boolean
  /** Content is clipped below the visible area — there is more to scroll to. */
  hasBelow: boolean
}

/** Sub-pixel slack so a container scrolled to its end doesn't report a
 * fractional remainder as "more content below". */
const EPSILON = 1

/**
 * Tracks which vertical edges of a scroll container are hiding content, so a
 * caller can render an overflow affordance (a fade mask, a chevron) instead of
 * relying on the native scrollbar.
 *
 * Re-measures after every render as well as on scroll and resize: rail items
 * come and go with the case's extensions, and expanding sections animate their
 * height, neither of which a container-only ResizeObserver would catch.
 */
export function useScrollEdges(ref: RefObject<HTMLElement | null>): ScrollEdges {
  const [edges, setEdges] = useState<ScrollEdges>({ hasAbove: false, hasBelow: false })

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return

    const measure = () => {
      const hasAbove = el.scrollTop > EPSILON
      const hasBelow = el.scrollTop + el.clientHeight < el.scrollHeight - EPSILON
      setEdges(prev =>
        prev.hasAbove === hasAbove && prev.hasBelow === hasBelow ? prev : { hasAbove, hasBelow }
      )
    }

    measure()
    el.addEventListener('scroll', measure, { passive: true })
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    for (const child of el.children) observer.observe(child)

    return () => {
      el.removeEventListener('scroll', measure)
      observer.disconnect()
    }
  })

  return edges
}
